import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ApiEnvelope, CreateExportRequest, ExportSummary } from '@seller-portal/types';
import { api } from '@/services/api';

/** Server-side exports: started from any page, listed by the shell's tray, downloaded by signed link. */

export const exportsKey = (sellerId: string) => ['seller', sellerId, 'exports'] as const;

/** The seller's recent exports; polls every 3 s while one is still being built. */
export function useExports(sellerId: string) {
  return useQuery({
    queryKey: exportsKey(sellerId),
    queryFn: async () =>
      (await api.get<ApiEnvelope<ExportSummary[]>>(`/sellers/${sellerId}/exports`)).data.data,
    refetchInterval: (query) =>
      query.state.data?.some((e) => e.status === 'running') ? 3000 : false,
  });
}

export function useStartExport(sellerId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (request: CreateExportRequest) =>
      (await api.post<ApiEnvelope<ExportSummary>>(`/sellers/${sellerId}/exports`, request)).data
        .data,
    onSuccess: (started) => {
      queryClient.setQueryData<ExportSummary[]>(exportsKey(sellerId), (list) => [
        started,
        ...(list ?? []).filter((e) => e.id !== started.id),
      ]);
      window.dispatchEvent(new CustomEvent('merx:exports-open'));
    },
  });
}

/** Asks for a signed link (the server records the download) and opens it. */
export async function downloadExportFile(
  sellerId: string,
  exportId: string,
  partNo: number
): Promise<void> {
  const res = await api.post<ApiEnvelope<{ url: string }>>(
    `/sellers/${sellerId}/exports/${exportId}/files/${partNo}/download`
  );
  window.location.assign(res.data.data.url);
}
